import { toast } from "sonner";
import { API_ENDPOINTS, HTTP_METHODS } from "../config/api";
import { BaseApiService, ApiResponse, PaginationInfo } from "./baseApi";

export interface Customer {
  id: number;
  customerName: string;
  customerCode: string;
  firmName: string;
  gstNo: string;
  pan_no: string;
  mobileNo: string;
  landLineNo: string;
  bankName: string;
  ifscCode: string;
  bankAccountNo: string;
  address: string;
  email: string;
  vendorCode: string;
  stateName: string;
  stateCode: string;
  country?: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

class CustomerApiService extends BaseApiService {

  // Get all customers with pagination
  async getCustomers(page: number = 1, size: number = 20): Promise<ApiResponse<Customer[]>> {
    try {
      // console.log('CustomerApiService: Making request to:', API_ENDPOINTS.CUSTOMERS.LIST);

      const params = new URLSearchParams({
        page: page.toString(),
        size: size.toString(),
      });

      const response = await this.makeRequest<ApiResponse<Customer[]>>(`${API_ENDPOINTS.CUSTOMERS.LIST}?${params.toString()}`);
      
      // console.log('CustomerApiService: Response received:', response);
      return response;
    } catch (error) {
      // console.error('CustomerApiService: Error fetching customers:', error);
      toast.error('Failed to fetch customers');
      throw error;
    }
  }

  // Get customer by ID
  async getCustomerById(id: string): Promise<ApiResponse<Customer>> {
    try {
      const response = await this.makeRequest<ApiResponse<Customer>>(API_ENDPOINTS.CUSTOMERS.DETAIL(id));
      return response;
    } catch (error) {
      toast.error('Failed to fetch customer details');
      throw error;
    }
  }

  // Create new customer
  async createCustomer(customerData: Omit<Customer, 'id' | 'createdAt' | 'updatedAt' | 'customerCode'>): Promise<ApiResponse<Customer>> {
    try {
      const response = await this.makeRequest<ApiResponse<Customer>>(API_ENDPOINTS.CUSTOMERS.CREATE, {
        method: HTTP_METHODS.POST,
        body: JSON.stringify(customerData),
      });
      toast.success('Customer created successfully!');
      return response;
    } catch (error) {
      toast.error('Failed to create customer');
      throw error;
    }
  }

  // Update customer
  async updateCustomer(id: string, customerData: Partial<Customer>): Promise<ApiResponse<Customer>> {
    try {
      const response = await this.makeRequest<ApiResponse<Customer>>(API_ENDPOINTS.CUSTOMERS.UPDATE(id), {
        method: HTTP_METHODS.PUT,
        body: JSON.stringify(customerData),
      });
      toast.success('Customer updated successfully!');
      return response;
    } catch (error) {
      toast.error('Failed to update customer');
      throw error;
    }
  }

  // Delete customer
  async deleteCustomer(id: string): Promise<ApiResponse<void>> {
    try {
      const response = await this.makeRequest<ApiResponse<void>>(API_ENDPOINTS.CUSTOMERS.DELETE(id), {
        method: HTTP_METHODS.DELETE,
      });
      toast.success('Customer deleted successfully!');
      return response;
    } catch (error) {
      toast.error('Failed to delete customer');
      throw error;
    }
  }

  // Search customers with pagination
  async searchCustomers(query: string, page: number = 1, size: number = 20): Promise<ApiResponse<Customer[]>> {
    try {
      const params = new URLSearchParams({
        q: query,
        page: page.toString(),
        size: size.toString(),
      });

      const response = await this.makeRequest<ApiResponse<Customer[]>>(`${API_ENDPOINTS.CUSTOMERS.SEARCH}?${params.toString()}`);
      
      return response;
    } catch (error) {
      toast.error('Failed to search customers');
      throw error;
    }
  }

  /** GET /sales/states — returns list of { name, code } for state dropdown */
  async getStates(): Promise<{ name: string; code: string }[]> {
    try {
      const data = await this.makeRequest<{ name: string; code: string }[] | { data?: { name: string; code: string }[]; content?: { name: string; code: string }[] }>(API_ENDPOINTS.CUSTOMERS.STATES);
      const list = Array.isArray(data) ? data : (data as any)?.data ?? (data as any)?.content ?? [];
      return Array.isArray(list) ? list : [];
    } catch (error) {
      toast.error('Failed to load states');
      throw error;
    }
  }
}


export const customerApiService = new CustomerApiService(); 